import React, {useMemo, useRef, useState} from 'react'
import Text from '@component/Text/Text'
import {apiLeagueJoin, ILeague, LeagueJoinApiStatus} from '@api/rank/league/league'
import {useTranslation} from '@lib/intl/useTranslations'
import useFormatDate from '@hook/useFormatDate'
import Spinner from '@component/Spinner/Spinner'
import LeagueJoinStatusModal from '@feature/leaderboard/components/LeagueJoinStatusModal/LeagueJoinStatusModal'
import useFormatNumber from '@hook/useFormatNumber'
import LeagueLeftTimeTag from '@feature/leaderboard/pages/ScreenLeague/LeagueLeftTimeTag/LeagueLeftTimeTag'
import useLogin from '@hook/useLogin'
import {useLoginModalStore} from '@store/LoginModalStore'
import useQueryGetExchanges from '@hook/query/core/exchange/useQueryGetExchanges'
import LeagueLiveTag from '@feature/leaderboard/pages/ScreenLeague/LeagueLiveTag/LeagueLiveTag'
import {addImageWebpQuery} from '@util/images'

import defaultBannerPc from '@image/league/league_default_banner.webp'
import defaultBannerMo from '@image/league/league_default_banner_mo.webp'
import bitgetBannerPc from '@image/league/league_bitget_banner.webp'
import bithumbBannerPc from '@image/league/league_bithumb_banner.webp'
import bitmexBannerPc from '@image/league/league_bitmex_banner.webp'
import bybitBannerPc from '@image/league/league_bybit_banner.webp'
import okxBannerPc from '@image/league/league_okx_banner.webp'
import phemexBannerPc from '@image/league/league_phemex_banner.webp'
import bitgetBannerMo from '@image/league/league_bitget_banner_mo.webp'
import bithumbBannerMo from '@image/league/league_bithumb_banner_mo.webp'
import bitmexBannerMo from '@image/league/league_bitmex_banner_mo.webp'
import bybitBannerMo from '@image/league/league_bybit_banner_mo.webp'
import okxBannerMo from '@image/league/league_okx_banner_mo.webp'
import phemexBannerMo from '@image/league/league_phemex_banner_mo.webp'
import useSnackbar from '@hook/useSnackbar'
import {createLog} from '@util/logs'
import IconCheck from '@svg/common/ic_check.svg'
import Link from 'next/link'
import useWindowSize from '@hook/useWindowSize'

interface IProps {
    leagueId?: string
    league?: ILeague
    leagueIsLoading?: boolean
    leagueRefetch?: () => void
}

const LeagueSummaryBoard: React.FC<IProps> = ({leagueId, league, leagueIsLoading, leagueRefetch}) => {
    const t = useTranslation()

    const {isSm} = useWindowSize()

    const {displayDate} = useFormatDate()
    const {displayNumber} = useFormatNumber()

    const getLeagueBannerUrl = useMemo(() => {
        if (league) {
            if (league?.meta?.banner_pc) {
                return addImageWebpQuery(league?.meta?.banner_pc, 1920, 200)
            } else {
                switch (league?.filter?.exchange) {
                    case 'bitget':
                        return bitgetBannerPc.src
                    case 'bithumb':
                        return bithumbBannerPc.src
                    case 'bitmex':
                        return bitmexBannerPc.src
                    case 'bybit':
                        return bybitBannerPc.src
                    case 'okx':
                        return okxBannerPc.src
                    case 'phemex':
                        return phemexBannerPc.src
                    default:
                        return defaultBannerPc.src
                }
            }
        }
    }, [league])

    const getLeagueBannerUrlMobile = useMemo(() => {
        if (league) {
            if (league?.meta?.banner_mobile) {
                return addImageWebpQuery(league?.meta?.banner_mobile, 956, 200)
            }
            switch (league?.filter?.exchange) {
                case 'bitget':
                    return bitgetBannerMo.src
                case 'bithumb':
                    return bithumbBannerMo.src
                case 'bitmex':
                    return bitmexBannerMo.src
                case 'bybit':
                    return bybitBannerMo.src
                case 'okx':
                    return okxBannerMo.src
                case 'phemex':
                    return phemexBannerMo.src
                default:
                    return defaultBannerMo.src
            }
        }
    }, [league])

    const getDetailPageTarget = () => {
        if (league?.start_url?.includes('https://event.leagueoftraders.io/')) {
            return '_blank'
        } else {
            return '_self'
        }
    }

    return (
        <>
            {leagueIsLoading ? (
                <div></div>
            ) : (
                <div
                    className={`relative rounded-[5px] max-h-[200px] md:max-h-[100%] overflow-hidden border border-transparent01 dark:border-bg_dark_white01 bg-cover bg-no-repeat`}
                    style={{backgroundImage: `url(${isSm ? getLeagueBannerUrlMobile : getLeagueBannerUrl})`}}>
                    <div className={'relative  py-[20px] px-[40px] md:py-[15px] md:px-[15px] '}>
                        <div>
                            <LeagueLiveTag
                                currentLeagueId={leagueId}
                                id={league?.id}
                                end_at={league?.end_at}
                                start_at={league?.start_at}
                                type={'board'}
                            />
                        </div>
                        <div className={'flex items-center gap-x-[10px] md:block'}>
                            <div className={'flex-1'}>
                                <div className={'flex items-center gap-x-[10px] mt-[5px]'}>
                                    <Text
                                        className={`${
                                            league?.name?.length >= 35
                                                ? 'text-h5'
                                                : league?.name?.length > 24
                                                ? 'text-h3 leading-[36px] md:text-h3 sm:text-h3'
                                                : 'text-h2 md:text-h3'
                                        } font-bold text-white `}>
                                        {league?.name}
                                    </Text>
                                    <LeagueLeftTimeTag
                                        {...league}
                                        className={'md:absolute md:top-[15px] md:right-[15px]'}
                                    />
                                </div>

                                <Text className={'text-body2 text-white md:text-body3'}>
                                    {t('league.leaderboard.date', {
                                        start_at: displayDate(league?.start_at, t('dateFormat.dateAndTime')),
                                        end_at: displayDate(league?.end_at, t('dateFormat.dateAndTimeAndTimezone')),
                                    })}
                                </Text>
                            </div>
                            <div className={`mt-[8px] flex items-center gap-x-[10px] md:mt-[20px] md:justify-center`}>
                                <JoinButton league_id={leagueId} league={league} leagueRefetch={leagueRefetch} />
                                {league?.start_url ? (
                                    <Link
                                        href={league?.start_url}
                                        onClick={() => {
                                            createLog('event', 'league_click_more_Info', {league_id: leagueId})
                                        }}
                                        target={getDetailPageTarget()}
                                        style={{backgroundColor: league?.meta?.theme_color}}
                                        className={`w-full flex text-center items-center justify-center min-w-[140px] py-[10px] px-[30px] md:flex-1 rounded-full md:min-w-[auto] md:max-w-[150px] sm:px-0  ${
                                            league?.filter?.exchange ? 'bg-white' : 'bg-primary'
                                        }`}>
                                        <Text
                                            className={`text-btn w-full h-full ${
                                                league?.meta?.theme_color === '#FFFFFF' || league?.filter?.exchange
                                                    ? 'text-gray01'
                                                    : 'text-white'
                                            }`}
                                            data-cy={'btn_leaderboard_league_details'}>
                                            {t('league.leaderboard.viewButton')}
                                        </Text>
                                    </Link>
                                ) : null}
                            </div>
                        </div>

                        <div
                            className={
                                'mt-[23px] bg-white bg-opacity-10 rounded-[5px] py-[10px] flex items-center justify-between md:hidden'
                            }>
                            {/*Current Prize Pool*/}
                            <div className={'text-center flex-1'}>
                                <Text className={'text-body_comm text-white'}>{t('league.leaderboard.prizepool')}</Text>
                                <Text className={'text-ti1 text-white'}>{league?.prize_pool}</Text>
                            </div>

                            {/*Participants*/}
                            <div className={'text-center flex-1'}>
                                <Text className={'text-body_comm text-white'}>
                                    {t('league.leaderboard.participants')}
                                </Text>
                                <Text className={'text-ti1 text-white'}>{displayNumber(league?.members_count)}</Text>
                            </div>

                            {/*Minimum Balance*/}
                            <div className={'text-center flex-1'}>
                                <Text className={'text-body_comm text-white'}>
                                    {t('league.leaderboard.minimumBalance')}
                                </Text>
                                <Text className={'text-ti1 text-white'}>{league?.filter?.min_balance}</Text>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default LeagueSummaryBoard

const JoinButton = ({
    league_id,
    league,
    leagueRefetch,
}: {
    league_id: string
    league: ILeague
    leagueRefetch: () => void
}) => {
    const {getExchange} = useQueryGetExchanges()
    const t = useTranslation()
    const [isJoining, setIsJoining] = useState(false)
    const leagueJoinModalRef = useRef(null)

    const {showSnackbar} = useSnackbar()

    const {isLogin} = useLogin()
    const showLoginModal = useLoginModalStore(state => state.showLoginModal)

    const isLateToRegister = useMemo(() => {
        if (!league) return true
        const registerBy = new Date().getTime() / 1000 - league.register_by
        const endAt = new Date().getTime() / 1000 - league.end_at
        return registerBy > 0 || endAt > 0
    }, [league])

    const data = useMemo(() => {
        return league
    }, [league])

    if (isLateToRegister || !data) return null
    const onJoinNow = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (e?.cancelable) {
            e.preventDefault()
        }
        if (!isLogin()) {
            showLoginModal()
            return
        }

        if (data.is_joined) {
            showSnackbar(t('leaderboard.bottomsheet.alert.already'))
            return
        }
        setIsJoining(true)
        try {
            const res = await apiLeagueJoin(league_id)
            setIsJoining(false)
            void leagueRefetch()

            if (res.data?.success) {
                leagueJoinModalRef.current?.show({screen: 'success'})
                createLog('event', 'League_Join_Success', {league_id})
                return
            }
            //Minimum balance condition not met
            if (res.status === LeagueJoinApiStatus.RegisterByPassed) {
                leagueJoinModalRef.current?.show({
                    screen: 'failed',
                    error: t('league.leaderboard.modal.failed.error.registerByPassed'),
                })
                createLog('error', 'league_join_error', {reason: '리그 참가 마감'})
                return
            }
            if (res.status === LeagueJoinApiStatus.LockedDueToKeyDelete) {
                leagueJoinModalRef.current?.show({
                    screen: 'failed',
                    error: t('league.leaderboard.modal.failed.error.lockedKeyDeletion'),
                    title: t('league.leaderboard.modal.failed.lockedKeyDeletion.title'),
                })
                createLog('error', 'league_join_error', {reason: '리그에 참가한 API 삭제'})
                return
            }
            if (res.status === LeagueJoinApiStatus.MinimumBalance) {
                leagueJoinModalRef.current?.show({
                    screen: 'failed',
                    error: t('league.leaderboard.modal.failed.error.minimumBalance', {
                        exchange: getExchange(data.filter?.exchange)?.display_name || '',
                        min_balance: data.filter.min_balance || '',
                    }),
                    title: t('league.leaderboard.modal.failed.minimumBalance.title'),
                })
                createLog('error', 'league_join_error', {reason: '참가 최소 자산 부족'})
                return
            }
            if (res.status === LeagueJoinApiStatus.LeagueExchange) {
                leagueJoinModalRef.current?.show({
                    screen: 'failed',
                    error: t('league.leaderboard.modal.failed.error.leagueExchange', {
                        exchange: getExchange(data.filter?.exchange)?.display_name || '',
                    }),
                    title: t('league.leaderboard.modal.failed.leagueExchange.title'),
                    status: LeagueJoinApiStatus.LeagueExchange,
                })
                createLog('error', 'league_join_error', {reason: '리그 참여 가능한 거래소 API 없음'})
                return
            }

            leagueJoinModalRef.current?.show({
                screen: 'failed',
                error: t('league.leaderboard.modal.failed.error.unknown'),
            })
            createLog('error', 'league_join_error', {reason: '알 수 없는 서버 이슈 반환'})
            return
        } catch (e) {
            setIsJoining(false)
        }
    }

    const buttonClassName = () => {
        if (data?.is_joined) {
            return 'ring-[1px] ring-white'
        } else {
            return !data?.meta?.theme_color ? 'bg-primary' : ''
        }
    }

    return (
        <>
            <button
                style={{backgroundColor: !data?.is_joined ? league?.meta?.theme_color : ''}}
                className={`w-full flex items-center justify-center min-w-[140px] py-[10px] px-[30px] md:flex-1 rounded-full md:min-w-[auto] md:max-w-[150px] sm:px-0 ${buttonClassName()}`}
                onClick={onJoinNow}>
                {!data?.is_joined && (
                    <>
                        {isJoining ? (
                            <Spinner visible={isJoining} size={'smallest'} className={'mr-1'} color={'#FFFFFF'} />
                        ) : (
                            <Text
                                className={`${
                                    data?.meta?.theme_color === '#FFFFFF' || data?.filter?.exchange
                                        ? 'text-gray01'
                                        : 'text-white'
                                } text-btn`}>
                                {t('league.leaderboard.joinButton')}
                            </Text>
                        )}
                    </>
                )}
                {data?.is_joined && (
                    <>
                        <IconCheck className={`mr-2 fill-white w-[16px]`} />
                        <Text className={`text-white text-btn`}>{t('league.leaderboard.joinButtonSuccess')}</Text>
                    </>
                )}
            </button>
            <LeagueJoinStatusModal
                ref={leagueJoinModalRef}
                callbackFunc={() => {
                    leagueRefetch()
                }}
            />
        </>
    )
}
